import { useSyncExternalStore } from 'react';
import type { MonthlyRow } from '@/types';
import { realValue } from '@/lib/format';
import { axisEndYear, formatYearTick, maxYearOf, yearTicks } from '@/lib/yearAxis';

/** Theme-aware palette (mid tones legible on both light and dark). */
export const COLORS = {
  netWorth: '#0ea5e9',
  portfolio: '#22c55e',
  loan: '#ef4444',
  interest: '#f59e0b',
  apr: '#8b5cf6',
  afterTaxApr: '#a78bfa',
  ltv: '#ef4444',
  cushion: '#22c55e',
  borrow: '#0ea5e9',
  sell: '#f97316',
  danger: '#dc2626',
};

/**
 * Shared Recharts tooltip styling: a dark, low-prominence panel (not a white box) with
 * high-contrast text that reads clearly in both light and dark themes. The small per-series
 * color swatch is kept (it stays series-colored), so color cues survive while the text is
 * legible. Bar charts additionally pass `cursor={false}` to drop the column highlight box.
 */
export const TOOLTIP_PROPS = {
  contentStyle: {
    background: 'rgba(15, 23, 42, 0.92)', // slate-900, slightly translucent
    border: '1px solid #334155', // slate-700
    borderRadius: '0.5rem',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    fontSize: 12,
    padding: '6px 10px',
  },
  labelStyle: { color: '#f8fafc', fontWeight: 600, marginBottom: 2 }, // slate-50
  itemStyle: { color: '#e2e8f0' }, // slate-200 — readable, replaces low-contrast series text
  cursor: { stroke: '#64748b', strokeWidth: 1 }, // subtle line cursor for line/area charts
} as const;

/** Subscribe to the `dark` class on <html> so charts pick correct grid/axis colors. */
export function useIsDark(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const obs = new MutationObserver(cb);
      obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      return () => obs.disconnect();
    },
    () => document.documentElement.classList.contains('dark'),
    () => false,
  );
}

export interface ChartPoint {
  month: number;
  year: number;
  [key: string]: number;
}

/** Downsample a long series for rendering (full data is retained for tables/PDF, §10.4). */
export function downsample<T>(rows: T[], maxPoints = 180): T[] {
  if (rows.length <= maxPoints) return rows;
  const step = Math.ceil(rows.length / maxPoints);
  const out = rows.filter((_, i) => i % step === 0);
  const last = rows[rows.length - 1]!;
  if (out[out.length - 1] !== last) out.push(last);
  return out;
}

/** Apply the real-dollar toggle to a nominal value at a month. */
export function disp(value: number, month: number, showReal: boolean, inflation: number): number {
  return showReal ? realValue(value, month, inflation) : value;
}

/** Build chart points from monthly rows, applying a per-field mapping + real-dollar toggle. */
export function toPoints(
  rows: MonthlyRow[],
  fields: Record<string, (r: MonthlyRow) => number>,
  showReal: boolean,
  inflation: number,
  currencyFields: string[] = [],
): ChartPoint[] {
  return downsample(rows).map((r) => {
    const point: ChartPoint = { month: r.monthIndex, year: +(r.monthIndex / 12).toFixed(2) };
    for (const [key, fn] of Object.entries(fields)) {
      const raw = fn(r);
      point[key] = currencyFields.includes(key) ? disp(raw, r.monthIndex, showReal, inflation) : raw;
    }
    return point;
  });
}

/**
 * Recharts props for a year x-axis, shared by every time-series chart on screen and in print.
 *
 * `type="number"` is the load-bearing part. Left as a category axis, Recharts picks ticks from
 * the data points themselves, and since points are monthly the whole-year labels repeat.
 * The tick maths lives in `@/lib/yearAxis` so it is covered by unit tests.
 */
export function yearAxisProps(data: readonly { year?: number }[] | undefined) {
  const maxYear = maxYearOf(data);
  return {
    dataKey: 'year',
    type: 'number' as const,
    domain: [0, axisEndYear(maxYear)] as [number, number],
    ticks: yearTicks(maxYear),
    tickFormatter: formatYearTick,
  };
}
